import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { ensureListing, ensureTransaction } from '../../util/data';
import { txIsPaymentPending } from '../../util/transaction';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { fetchNumberOfPendingServiceFees, fetchNumberOfCartPreorders } from '../../ducks/user.duck';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  TransactionPanel, NamedRedirect,
} from '../../components';
import { TopbarContainer } from '../../containers';

import {
  acceptSale,
  declineSale,
  cancelSale,
  pickupSale,
  completeNotPickedupSale,
  completeSale,
  loadData,
  setInitialValues,
  sendMessage,
  sendReview,
  fetchMoreMessages,
  partialCancel,
  updateTransaction,
  changeDeliveryState, createServiceFee, clearServiceFee,
  updateProtocolInfo,
  signProtocol,
  updateTransactionOnly,
  updateReceiver,
  updateStolenStatus
} from './TransactionPage.duck';
import css from './TransactionPage.css';
import { categorizeTransactions } from './helpers.js';

const PROVIDER = 'provider';
const CUSTOMER = 'user';

// TransactionPage handles data loading for Sale and ServiceFeeSummary views to transaction pages in Inbox.
export const TransactionPageComponent = props => {
  const {
    currentUser,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    fetchMessagesError,
    fetchMessagesInProgress,
    totalMessagePages,
    oldestMessagePageFetched,
    fetchTransactionError,
    history,
    intl,
    messages,
    onManageDisableScrolling,
    onSendMessage,
    onSendReview,
    onShowMoreMessages,
    params,
    scrollingDisabled,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    transaction,
    transactionRole,
    acceptInProgress,
    acceptSaleError,
    cancelInProgress,
    cancelSaleError,
    declineInProgress,
    declineSaleError,
    pickupInProgress,
    pickupSaleError,
    completeNotPickedupInProgress,
    completeNotPickedupError,
    completeInProgress,
    completeSaleError,
    onAcceptSale,
    onDeclineSale,
    onCancelSale,
    onPickupSale,
    onCompleteSale,
    onCompleteNotPickedupSale,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    callSetInitialValues,
    onInitializeCardPaymentData,
    review,
    currentUserHasListings,
    currentUserProviderId,
    partialCancelInProgress,
    partialCancelError,
    partialCancelSuccess,
    onPartialCancelOrder,
    pageName,
    onUpdateTransaction,
    onChangeDeliveryState,
    changeDeliveryStateInProgress,
    canBeProlongedTo,
    createServiceFeeInProgress,
    createServiceFeeError,
    createServiceFeeSuccess,
    onCreateServiceFee,
    onClearServiceFee,
    transactionSuccessComponent,
    customerTransactions,
    onUpdateProtocolInfo,
    onSignProtocol,
    onUpdateTransactionOnly,
    onUpdateReceiver,
    signProtocolInProgress,
    signProtocolSuccess,
    signProtocolError,
    duplicatedPeselTransactions,
    onUpdateStolenStatus,
    markAsStolenInProgress,
    markAsStolenError
  } = props;

  const reviewNeeded = props.location.hash === '#review' && !review?.id?.uuid;
  const currentTransaction = ensureTransaction(transaction);
  const currentListing = ensureListing(currentTransaction.listing);
  const isProviderRole = transactionRole === PROVIDER;
  const isCustomerRole = transactionRole === CUSTOMER;
  const dispatch = useDispatch();
  const { completed, ongoing, future, overdue, notPaidServiceFees } = categorizeTransactions(customerTransactions, currentTransaction?.id?.uuid);

  useEffect(() => {
    if (pageName === 'OrderDetailsSuccessPage') {
      dispatch(fetchNumberOfPendingServiceFees());
      dispatch(fetchNumberOfCartPreorders())
    }
  }, []);


  const redirectToCheckoutPageWithInitialValues = (initialValues, listing) => {
    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, initialValues);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { cartId: 'prolonged' },
        {}
      )
    );
  };

  // If payment is pending, redirect to CheckoutPage
  if (
    txIsPaymentPending(currentTransaction) &&
    isCustomerRole &&
    currentTransaction.attributes.lineItems
  ) {
    return <NamedRedirect name="PaymentPage" params={{ id: currentTransaction.id.uuid }} />;
  }

  // Customer can create a booking, if the tx is in "enquiry" state.
  const handleSubmitBookingRequest = values => {
    const { bookingDates, ...bookingData } = values;

    const initialValues = {
      listing: currentListing,
      // enquired transaction should be passed to CheckoutPage
      transaction: currentTransaction,
      bookingData,
      bookingDates: {
        bookingStart: bookingDates.startDate,
        bookingEnd: bookingDates.endDate,
      },
      confirmPaymentError: null,
    };

    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  };

  const deletedListingTitle = intl.formatMessage({
    id: 'TransactionPage.deletedListing',
  });
  const listingTitle = currentListing.attributes.deleted
    ? deletedListingTitle
    : currentListing.attributes.title;

  // Redirect users with someone else's direct link to their own inbox/sales or inbox/orders page.
  const isDataAvailable =
    currentUser &&
    currentTransaction.id &&
    currentTransaction.id.uuid === params.id &&
    currentTransaction.attributes.lineItems &&
    currentTransaction.user &&
    currentTransaction.provider &&
    !fetchTransactionError;

  const detailsClassName = classNames(css.tabContent, css.tabContentVisible);

  const fetchErrorMessage = isCustomerRole
    ? 'TransactionPage.fetchOrderFailed'
    : 'TransactionPage.fetchSaleFailed';
  const loadingMessage = isCustomerRole
    ? 'TransactionPage.loadingOrderData'
    : 'TransactionPage.loadingSaleData';

  const loadingOrFailedFetching = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id={`${fetchErrorMessage}`} />
    </p>
  ) : (
    <p className={css.loading}>
      <FormattedMessage id={`${loadingMessage}`} />
    </p>
  );

  const initialMessageFailed = !!(
    initialMessageFailedToTransaction &&
    currentTransaction.id &&
    initialMessageFailedToTransaction.uuid === currentTransaction.id.uuid
  );

  // TransactionPanel is presentational component
  // that currently handles showing everything inside layout's main view area.

  const panel = isDataAvailable ? (
    <TransactionPanel
      className={detailsClassName}
      currentUser={currentUser}
      transaction={currentTransaction}
      fetchMessagesInProgress={fetchMessagesInProgress}
      totalMessagePages={totalMessagePages}
      oldestMessagePageFetched={oldestMessagePageFetched}
      messages={messages}
      review={review}
      initialMessageFailed={initialMessageFailed}
      savePaymentMethodFailed={savePaymentMethodFailed}
      fetchMessagesError={fetchMessagesError}
      sendMessageInProgress={sendMessageInProgress}
      completeNotPickedupInProgress={completeNotPickedupInProgress}
      sendMessageError={sendMessageError}
      sendReviewInProgress={sendReviewInProgress}
      sendReviewError={sendReviewError}
      onManageDisableScrolling={onManageDisableScrolling}
      onShowMoreMessages={onShowMoreMessages}
      onSendMessage={onSendMessage}
      onSendReview={onSendReview}
      transactionRole={transactionRole}
      onAcceptSale={onAcceptSale}
      onDeclineSale={onDeclineSale}
      onCancelSale={onCancelSale}
      onPickupSale={onPickupSale}
      onCompleteNotPickedupSale={onCompleteNotPickedupSale}
      onCompleteSale={onCompleteSale}
      acceptInProgress={acceptInProgress}
      declineInProgress={declineInProgress}
      cancelInProgress={cancelInProgress}
      pickupInProgress={pickupInProgress}
      completeInProgress={completeInProgress}
      acceptSaleError={acceptSaleError}
      declineSaleError={declineSaleError}
      cancelSaleError={cancelSaleError}
      pickupSaleError={pickupSaleError}
      completeSaleError={completeSaleError}
      nextTransitions={processTransitions}
      onSubmitBookingRequest={handleSubmitBookingRequest}
      timeSlots={timeSlots}
      fetchTimeSlotsError={fetchTimeSlotsError}
      currentUserHasListings={currentUserHasListings}
      currentUserProviderId={currentUserProviderId}
      history={history}
      partialCancelInProgress={partialCancelInProgress}
      partialCancelError={partialCancelError}
      onPartialCancelOrder={onPartialCancelOrder}
      partialCancelSuccess={partialCancelSuccess}
      pageName={pageName}
      onUpdateTransaction={onUpdateTransaction}
      reviewNeeded={reviewNeeded}
      onChangeDeliveryState={onChangeDeliveryState}
      changeDeliveryStateInProgress={changeDeliveryStateInProgress}
      canBeProlongedTo={canBeProlongedTo}
      callSetInitialValues={callSetInitialValues}
      onInitializeCardPaymentData={onInitializeCardPaymentData}
      createServiceFeeInProgress={createServiceFeeInProgress}
      createServiceFeeError={createServiceFeeError}
      createServiceFeeSuccess={createServiceFeeSuccess}
      onCreateServiceFee={onCreateServiceFee}
      onClearServiceFee={onClearServiceFee}
      transactionSuccessComponent={transactionSuccessComponent}
      completedTxs={completed}
      ongoingTxs={ongoing}
      futureTxs={future}
      overdueTxs={overdue}
      notPaidServiceFeesTxs={notPaidServiceFees}
      onUpdateProtocolInfo={onUpdateProtocolInfo}
      onSignProtocol={onSignProtocol}
      onUpdateTransactionOnly={onUpdateTransactionOnly}
      onUpdateReceiver={onUpdateReceiver}
      signProtocolInProgress={signProtocolInProgress}
      signProtocolSuccess={signProtocolSuccess}
      signProtocolError={signProtocolError}
      onUpdateStolenStatus={onUpdateStolenStatus}
      markAsStolenInProgress={markAsStolenInProgress}
      markAsStolenError={markAsStolenError}
      duplicatedPeselTransactions={duplicatedPeselTransactions}
    />
  ) : (
    loadingOrFailedFetching
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'TransactionPage.title' }, { title: listingTitle })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
         <div>{panel}</div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter className={css.footer}>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

TransactionPageComponent.defaultProps = {
  currentUser: null,
  fetchTransactionError: null,
  acceptSaleError: null,
  declineSaleError: null,
  transaction: null,
  fetchMessagesError: null,
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  transactionSuccessComponent: null,
};

const mapStateToProps = state => {
  const {
    fetchTransactionError,
    acceptSaleError,
    declineSaleError,
    cancelSaleError,
    pickupSaleError,
    completeSaleError,
    acceptInProgress,
    declineInProgress,
    cancelInProgress,
    completeNotPickedupInProgress,
    pickupInProgress,
    completeInProgress,
    transactionRef,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    review,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    partialCancelInProgress,
    partialCancelError,
    partialCancelSuccess,
    changeDeliveryStateInProgress,
    canBeProlongedTo,
    createServiceFeeInProgress,
    createServiceFeeError,
    createServiceFeeSuccess,
    customerTransactionRefs,
    signProtocolInProgress,
    signProtocolSuccess,
    signProtocolError,
    duplicatedPeselTransactionRefs,
    markAsStolenInProgress,
    markAsStolenError
  } = state.TransactionPage;
  const { currentUser,  currentUserHasListings, userProviders } = state.user;
  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;
  const customerTransactions = getMarketplaceEntities(state, customerTransactionRefs ? customerTransactionRefs : []);
  const duplicatedPeselTransactions = getMarketplaceEntities(state, duplicatedPeselTransactionRefs ? duplicatedPeselTransactionRefs : []);

  return {
    currentUser,
    fetchTransactionError,
    acceptSaleError,
    declineSaleError,
    cancelSaleError,
    pickupSaleError,
    completeSaleError,
    acceptInProgress,
    declineInProgress,
    cancelInProgress,
    pickupInProgress,
    completeInProgress,
    completeNotPickedupInProgress,
    scrollingDisabled: isScrollingDisabled(state),
    transaction,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    review,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    currentUserHasListings,
    currentUserProviderId: userProviders && !!userProviders.length && userProviders[0].id.uuid,
    partialCancelInProgress,
    partialCancelError,
    partialCancelSuccess,
    changeDeliveryStateInProgress,
    canBeProlongedTo,
    createServiceFeeInProgress,
    createServiceFeeError,
    createServiceFeeSuccess,
    customerTransactions,
    signProtocolInProgress,
    signProtocolSuccess,
    signProtocolError,
    duplicatedPeselTransactions,
    markAsStolenInProgress,
    markAsStolenError
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onAcceptSale: transactionId => dispatch(acceptSale(transactionId)),
    onDeclineSale: transactionId => dispatch(declineSale(transactionId)),
    onCancelSale: transactionId => dispatch(cancelSale(transactionId)),
    onPickupSale: transactionId => dispatch(pickupSale(transactionId)),
    onCompleteNotPickedupSale: transactionId => dispatch(completeNotPickedupSale(transactionId)),
    onCompleteSale: transactionId => dispatch(completeSale(transactionId)),
    onUpdateTransaction: transactionId => dispatch(updateTransaction(transactionId)),
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
    onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
    onPartialCancelOrder: (transactionId, items) => dispatch( partialCancel(transactionId, items)),
    onChangeDeliveryState: (transactionId, deliveryState) => dispatch(changeDeliveryState(transactionId, deliveryState)),
    onCreateServiceFee: (serviceFeeFor, code, amount, reason, txRole) => dispatch(createServiceFee(serviceFeeFor, code, amount, reason, txRole)),
    onClearServiceFee: () => dispatch(clearServiceFee()),
    onUpdateProtocolInfo: (params) => dispatch(updateProtocolInfo(params)),
    onSignProtocol: (params) => dispatch(signProtocol(params)),
    onUpdateTransactionOnly: params => dispatch(updateTransactionOnly(params)),
    onUpdateReceiver: params => dispatch(updateReceiver(params)),
    onUpdateStolenStatus: (id, isStolen) => dispatch(updateStolenStatus(id, isStolen))
  };
};

const TransactionPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(TransactionPageComponent);

TransactionPage.loadData = loadData;
TransactionPage.setInitialValues = setInitialValues;

export default TransactionPage;
